import { get, isEmpty, isUndefined } from 'lodash';
import React, { lazy, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FaExclamationCircle } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { ReactComponent as WhatsAppIcon } from '../../../assets/images/icons/whatsapp.svg';
import { ReactComponent as DoubtIcon } from '../../../assets/images/icons/mevo/ic-doubt.svg';
import BaseDialog from '../../../components/Dialogs/BaseDialog';
import { MobileDefaultBottom } from '../../../components/MobileDefaultBottom';
import SearchAddressByCep from '../../../components/SearchAddressByCep';
import SelectGender from '../../../components/SelectGender';
import SelectState from '../../../components/SelectState';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { FormControl } from '../../../components/UI-CHAKRA/FormControl';
import { FormErrorMessage } from '../../../components/UI-CHAKRA/FormErrorMessage';
import { FormLabel } from '../../../components/UI-CHAKRA/FormLabel';
import { Input } from '../../../components/UI-CHAKRA/Input/Input';
import { MASKS as CHAKRA_UI_MASKS, MASKS, MaskedInput } from '../../../components/UI-CHAKRA/Input/MaskedInput';
import { Switch } from '../../../components/UI-CHAKRA/Switch';
import { Text } from '../../../components/UI-CHAKRA/Text';
import withSuspense from '../../../components/withLazyComponent';
import { DateFromFormat, DateFromISOToBrazilianFormat, dateOneGreaterThanDateTwo } from '../../../helpers/Date';
import { logger } from '../../../helpers/logger';
import { Portal } from '../../../helpers/Portal';
import { formatCpf, onlyDigits } from '../../../helpers/string';
import { toast } from '../../../helpers/toast';
import { useIsMobile } from '../../../helpers/useIsMobile';
import { useLoading } from '../../../helpers/useLoading';
import { validateBirthDate } from '../../../helpers/validations';
import { waitAtLeast } from '../../../helpers/waitAtLeast';
import { safeGetFromLocalStorage, safeSetInLocalStorage, scrollTo } from '../../../helpers/window';
import { EmissionProviderForm } from '../../../providers/EmissionProvider';
import { DialogWrappedComponentProps, FeedbackContext } from '../../../providers/FeedbackProvider';
import { PrescriptionContext } from '../../../providers/PrescriptionProvider';
import { Address } from '../../../shared/cep';
import { Dialog, DialogContent, PRESCRIBER_DIALOG_TYPES, PrescriberService } from '../../../shared/prescriber';
import { themeColors } from '../../../shared/theme/themeColors';
import { Tracker } from '../../../shared/tracker/Tracker';
import {
  authenticateCertiSign,
  authenticateDinamo,
  authenticateRemoteId,
  authenticateSafeweb,
} from '../../certificate/AuthenticateCloudCertificate';
import { CERTIFICATES, CERTIFIER_STATUS, isCertifierUnstable } from '../../certificate/certificates';
import { CertificateService, RemoteIDSessionData, VIDAAS_DAYS_LIFE } from '../../certificate/certificateService';
import LMEBadge from '../../LME/LMEBadge';
import { Icd } from '../../my-central/patientService';
import InteractionBadge from '../JustifyInteractionsDialog/InteractionBadge';
import JustifyInteractionsDialog from '../JustifyInteractionsDialog/JustifyInteractionsDialog';
import PrescriptionEmissionLoading from '../PrescriptionEmissionLoading/PrescriptionEmissionLoading';
import PrescriptionLayout from '../PrescriptionLayout';
import { PrescriptionService, PrescriptionValidation } from '../service';
import WhatsAppDialogConsent from '../WhatsAppDialogConsent';
import { AuthenticateWithPin } from './AuthenticateWithPin';
import { DialogAskForPrintOrDigitalCertificate } from './DialogAskForPrintOrDigitalCertificate';
import { PDFPreview } from './PDFPreview';
import { PrescriptionSignedNotification } from './PrescriptionSignedNotification';

import { Divider, RadioGroup, useBreakpointValue } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { Checkbox } from '../../../components/UI-CHAKRA/Checkbox';
import SearchIcd from '../../../components/UI-CHAKRA/SearchIcd';
import { useApacBpaStore } from '../../../stores/apacBpaStore';
import { useInteractionStore } from '../../../stores/interactionStore';
import { usePathStore } from '../../../stores/pathStore';
import { ClosePrescriptionPayload, usePrescriptionStore } from '../../../stores/prescriptionStore';
import { useSmrStore } from '../../../stores/smrStore';
import ApacBpaBadge from '../../apac-bpa/ApacBpaBadge';
import { CreateEditLayout, LAYOUT_ACTION_TYPES } from '../../my-central/Prescriber/PrescriberCentral';
import { EstablishmentAddressForm } from './EstablishmentAddressForm';

import { DateTime } from 'luxon';
import BannerMevoNotification from '../../../assets/images/Mevo_Banner_notificacao3.png';
import { Anchor } from '../../../components/notifications/Anchor';
import { ANCHORS, ANCHOR_POS } from '../../../components/notifications/notifications';
import { PhoneInput } from '../../../components/PhoneInput/PhoneInput';
import PrescriptionSigningFlows from '../../../components/PrescriptionSigningFlows';
import CertificateList from '../../../components/PrescriptionSigningFlows/CertificateList';
import LocalCertificateFlow from '../../../components/PrescriptionSigningFlows/LocalCertificateFlow';
import SelectCountryAutocomplete from '../../../components/SelectCountryAutocomplete/selectCountryAutocomplete';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from '../../../components/UI-CHAKRA/Accordion';
import { PATIENT_FOREIGNER, PRESCRIPTION_STATUS } from '../../../interfaces/prescription';
import { countriesData, getCountryByName } from '../../../shared/countries';
import { DONT_SHOW_RQE_IN_PDF_KEY } from '../../my-central/constants';
import PrintJustifyDialog, { JustificationOptions } from '../PrintJustifyDialog';
import { TrackerEventService } from '../trackerEventService';
import { DialogDuplicatedPrescription } from './DialogDuplicatedPrescription';
import { DialogRetainSignPrescription } from './DialogRetainSignedPrescription';
import { PrescriptionCertificateEnum } from './PrecriptionCertificateEnum';
import { PatientEstablishmentCard } from './PatientEstablishmentCard';
import { PrescriptionSendingMethodInformative } from './PrescriptionSendingMethodInformative';
import { Radio } from '../../../components/UI-CHAKRA/Radio';
import { Tooltip } from '../../../components/UI-CHAKRA/Tooltip';
import { Grid } from '../../../components/UI-CHAKRA/Grid';

const PrescriptionSummary = withSuspense(lazy(() => import('./PrescriptionSummary/PrescriptionSummary')));
const PDFLayoutEditor = withSuspense(lazy(() => import('../../my-central/Prescriber/PDFLayoutEditor')));
const PDFLayoutList = withSuspense(lazy(() => import('../../my-central/Prescriber/PDFLayoutList')));
const DatePicker = withSuspense(lazy(() => import('../../../components/DatePicker')));

const FIVE_SECONDS_IN_MS = 5000;

function PrescriptionEmission() {
  const { localCertificateValidation, loadingPrescriberCoreData } = useContext(PrescriptionContext);

  const {
    prescription,
    selectedCertificateType,
    selectedLocalCertificate,
    prescriberConfigurations,
    setPrescriberConfigurations,
    closePrescription,
    emittedTo,
    setEmittedTo,
    shouldHideCertificates,
    shouldBypassCertificateFlow,
    setSelectedCertificateType,
  } = usePrescriptionStore();

  const selectedRqe = usePrescriptionStore((store) => store.selectedRqe);

  const { path } = usePathStore();

  const { prescriberConfiguration } = usePrescriptionStore();

  const doesPrescriptionHasSigningWarning = usePrescriptionStore((store) => store.doesPrescriptionHasSigningWarning());
  const prescriptionObservation = usePrescriptionStore((store) => store.prescriptionObservation());
  const partnerJustifyInteraction = usePrescriptionStore((store) => store.partnerJustifyInteraction());
  const ownedCertificates = usePrescriptionStore((store) => store.ownedCertificates);

  const { setJustifiedInteractions } = useInteractionStore();

  const isMobile = useIsMobile('md');

  const { smrForm } = useSmrStore();
  const { completedForm } = useApacBpaStore();

  const [loadingClosePrescription, runWithLoadingClosePrescription] = useLoading();
  const [_, runWithLoadingCertificates] = useLoading();
  const [loadingPrescriptionValidation, runWithloadingPrescriptionValidation] = useLoading();

  const history = useHistory();

  const [prescriptionValidation, setPrescriptionValidation] = useState<PrescriptionValidation>();

  const [showWhatsAppDialogConsent, setShowWhatsAppDialogConsent] = useState(false);
  const [shouldSendPrescriptionWhatsApp, setShouldSendPrescriptionWhatsApp] = useState(false);
  const [hasWhatsAppConsent, setHasWhatsAppConsent] = useState(false);
  const [emitted, setEmitted] = useState(false);
  const [Modal, setModal] = useState<React.FC | null>(null);
  const [loadingCertificationSignatureSession, runWithLoadingCertificationSignatureSession] = useLoading();
  const [createEditLayout, setCreateEditLayout] = useState<CreateEditLayout | null>(null);
  const [houseNumberHasNoNumber, setHouseNumberHasNoNumber] = useState(false);
  const [patientWithoutCpf, setPatientWithoutCpf] = useState(false);
  const [showCpfTooltipMobile, setShowCpfTooltipMobile] = useState(false);

  const [openChangeDate, setOpenChangeDate] = useState(false);

  const form = useFormikContext<EmissionProviderForm>();

  const icd = form.values.icd;

  const setFieldValue = useMemo(() => form.setFieldValue, [form.setFieldValue]);
  const validateField = useMemo(() => form.validateField, [form.validateField]);

  const needToFillAddress = form.values.patientNeedToFillAddress;
  const needToFillBirthdate = true;
  const needToFillDocument = true;
  const needToFillGender = form.values.patientNeedToFillGender;
  const needToFillIcd = form.values.patientNeedToFillIcd;

  const needToFillEstablishmentAddress = form.values.establishmentNeedToFill;
  const patientName = useMemo(() => prescription?.Paciente.Nome, [prescription?.Paciente.Nome]);

  const { openBaseDialog } = useContext(FeedbackContext);

  const enablePrintingForms = get(prescriberConfiguration, 'Features.enablePrintingForms', false);
  const enableCustomPdfLayout = get(prescriberConfiguration, 'Features.enablePdfCustomLayout', false);
  const enablePrescriptionSummary = get(prescriberConfiguration, 'Features.enablePrescriptionSummary', false);
  const disableSelectShowDateInPdf = get(prescriberConfiguration, 'Features.disableSelectShowDateInPdf', false);
  const disablePatientAppSwitch = get(prescriberConfiguration, 'Features.disablePatientAppSwitch', false);
  const enableDialogCloseWindowTab = get(prescriberConfiguration, 'Features.enableDialogCloseWindowTab', false);
  const showMevoThesisBanner = get(prescriberConfiguration, 'Features.showMevoThesisBanner', false);
  const enableIssueMedicinesInSeparateTabsSwitch = get(
    prescriberConfiguration,
    'Features.enableIssueMedicinesInSeparateTabs',
    false
  );

  const enableDuplicatedPrescriptionAlert = get(
    prescriberConfiguration,
    'Features.enableDuplicatedPrescriptionAlert',
    false
  );

  const dialogWidth = useBreakpointValue({ base: '95%', md: '575px' });

  const userCertificates = usePrescriptionStore((store) => store.userCertificates());

  const enableMandatoryDigitalCertificate = useMemo(() => {
    if (
      !userCertificates ||
      userCertificates.length === 0 ||
      !userCertificates.some((cert) => cert.value !== CERTIFICATES.MANUAL)
    )
      return false;
    if (shouldBypassCertificateFlow) return false;
    return get(prescriberConfiguration, 'Features.mandatoryDigitalCertificate', false);
  }, [prescriberConfiguration, shouldBypassCertificateFlow, userCertificates]);

  const enableChangePrescriptionEmissionDate = get(
    prescriberConfiguration,
    'Features.enableChangePrescriptionEmissionDate',
    false
  );

  const disableWhatsapp = get(prescriberConfiguration, 'Features.disableWhatsapp', false);
  const sendSMS = get(prescriberConfiguration, 'EnviarSMSPrescricao', false);
  const disableEmail = get(prescriberConfiguration, 'Features.disableEmailInputField', false);
  const blockWhatsappWeb = get(prescriberConfiguration, 'Features.blockWhatsAppWeb', false);
  const [lastCertificateHasError, setLastCertificateHasError] = useState(false);

  const hasAnyPortaria344 = useMemo(() => {
    return prescription?.Itens?.some((medicine) => medicine.idPortaria344 !== 0);
  }, [prescription]);

  const enableMandatoryCpfPortaria344 = form.values.enableMandatoryCpfPortaria344;

  const [showPatientIsForeigner, setShowPatientIsForeigner] = useState<PATIENT_FOREIGNER | null>(null);

  useEffect(() => {
    setFieldValue('hasPortaria344', hasAnyPortaria344 || false, false);
  }, [hasAnyPortaria344, setFieldValue]);

  useEffect(() => {
    if (prescription?.Paciente?.Passaporte?.Numero && !prescription?.Paciente?.Documento) {
      setPatientWithoutCpf(true);
      setShowPatientIsForeigner(PATIENT_FOREIGNER.FOREIGNER);
      setFieldValue('patientWithoutCpf', true, false);
      setFieldValue('patientPassport', prescription.Paciente.Passaporte.Numero, false);
      if (prescription.Paciente.Passaporte.PaisEmissor) {
        const foundCountry = getCountryByName(prescription.Paciente.Passaporte.PaisEmissor.trim());
        if (foundCountry) {
          setFieldValue('patientCountry', foundCountry.id, false);
        }
      }
    }
  }, [prescription?.Paciente?.Passaporte, prescription?.Paciente?.Documento, setFieldValue]);

  const MEVO_ANVISA_URL =
    'https://mevo.com.br/blog/mevo-explica-tudo-o-que-voce-precisa-saber-sobre-a-nova-norma-da-anvisa-para-receituarios-eletronicos';

  useEffect(() => {
    if (enableChangePrescriptionEmissionDate) {
      const dateNow = new Date();
      const dateForEmission = emittedTo ?? new Date();
      if (dateOneGreaterThanDateTwo(dateForEmission, dateNow)) {
        dateForEmission.setHours(0);
        dateForEmission.setMinutes(1);
        dateForEmission.setSeconds(0);
        dateForEmission.setMilliseconds(0);

        setEmittedTo(dateForEmission);
      }
    }
  }, [enableChangePrescriptionEmissionDate, setEmittedTo, emittedTo]);

  const { justifiedInteractions } = useInteractionStore();

  const showPrintOption = useMemo(() => {
    if (!prescription || !prescriberConfiguration || isMobile) return false;

    const featureDisableOpenPdfSwitch = get(prescriberConfiguration, 'Features.disableOpenPdfSwitch', false);

    if (featureDisableOpenPdfSwitch) return false;

    return (
      prescription.PermitirImpressao &&
      !(prescriberConfiguration.AssinarViaProntuario && prescriberConfiguration.RemoverOpcaoAssinatura)
    );
  }, [isMobile, prescriberConfiguration, prescription]);

  const doesPrescriptionHasLME = useMemo(() => {
    return prescription?.Itens?.some((medicine) => medicine.medicamentoLme);
  }, [prescription]);

  useEffect(() => {
    if (!prescription || !prescriberConfiguration) return;

    if (doesPrescriptionHasLME) {
      setFieldValue('generatePdf', true, false);
      return;
    }

    if (!showPrintOption) {
      setFieldValue('generatePdf', false, false);
      return;
    }

    const defaultValue = get(prescriberConfiguration, 'Features.defaultValueGeneratePdf', null);

    if (defaultValue) {
      setFieldValue('generatePdf', defaultValue, false);
      return;
    }

    setFieldValue('generatePdf', selectedCertificateType === CERTIFICATES.MANUAL, false);
  }, [
    doesPrescriptionHasLME,
    prescriberConfiguration,
    prescription,
    selectedCertificateType,
    setFieldValue,
    showPrintOption,
  ]);

  useEffect(() => {
    if (!prescriptionValidation) return;
    const val = Object.values(prescriptionValidation.Paciente.Endereco).some((field) => !field);
    setFieldValue('patientNeedToFillAddress', val, false);
    setFieldValue('patientNeedToFillNumber', val, false);

    const establishmentHasAddress = get(prescriptionValidation, 'Estabelecimento.Endereco', true);
    setFieldValue('establishmentNeedToFill', !establishmentHasAddress, false);
  }, [prescriptionValidation, setFieldValue]);

  useEffect(() => {
    if (!prescriptionValidation || !prescription) return;

    const val =
      !prescriptionValidation.Paciente.DataNascimento &&
      !validateBirthDate(DateFromISOToBrazilianFormat(prescription.Paciente.Nascimento || ''));

    setFieldValue('patientNeedToFillBirthdate', val, false);
  }, [prescription, prescriptionValidation, setFieldValue]);

  useEffect(() => {
    if (!prescriptionValidation || !prescription) return;
    const val = !prescriptionValidation.Paciente.Sexo;
    setFieldValue('patientNeedToFillGender', val, false);
  }, [prescriptionValidation, prescription, setFieldValue]);

  useEffect(() => {
    if (!prescriptionValidation) return;
    setFieldValue('patientNeedToFillIcd', !prescriptionValidation.Paciente.CID, false);
  }, [prescriptionValidation, setFieldValue]);

  useEffect(() => {
    if (!prescriberConfigurations) return;

    setFieldValue('showDateInPdf', prescriberConfigurations.ExibirDataPdf, false);
  }, [prescriberConfigurations, setFieldValue]);

  useEffect(() => {
    if (!prescriberConfigurations) return;

    if (selectedCertificateType === CERTIFICATES.MANUAL) {
      setFieldValue('openPatientApp', false, false);
      return;
    }

    if (disablePatientAppSwitch) {
      setFieldValue('openPatientApp', false, false);
      return;
    }

    const value = get(prescriberConfigurations, 'Configuracoes.openPatientApp', true);

    setFieldValue('openPatientApp', value, false);
  }, [disablePatientAppSwitch, prescriberConfigurations, selectedCertificateType, setFieldValue]);

  useEffect(() => {
    if (!prescriberConfigurations) return;

    const value = get(prescriberConfigurations, 'Configuracoes.issueMedicinesInSeparateTabs', false);

    setFieldValue('issueMedicinesInSeparateTabs', value, false);
  }, [disablePatientAppSwitch, prescriberConfigurations, selectedCertificateType, setFieldValue]);

  const signInEhr = useMemo(() => {
    return !!prescriberConfiguration?.AssinarViaProntuario;
  }, [prescriberConfiguration]);

  useEffect(() => {
    const run = async () => {
      if (!prescription) return;
      runWithloadingPrescriptionValidation(async () => {
        const validation = await PrescriptionService.validatePrescription(prescription.idPrescricao);
        setPrescriptionValidation(validation);
      });
    };

    run();
  }, [prescription, runWithloadingPrescriptionValidation]);

  const isDigitalCertificateSelected = useMemo(() => {
    return ![CERTIFICATES.MANUAL, CERTIFICATES.NOT_DEFINED, null].includes(selectedCertificateType) || signInEhr;
  }, [selectedCertificateType, signInEhr]);

  const onChangeGeneratePdf = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fieldState = event.target.checked;

      const onIgnore = (onClose?: () => void) => {
        setFieldValue('generatePdf', fieldState);

        if (fieldState) {
          Tracker.trackEvent({
            event: 'Open PDF Selected',
          });
        }

        if (onClose) onClose();
      };

      if (
        isDigitalCertificateSelected &&
        event.target.checked &&
        !get(prescriberConfiguration, 'Features.disableSignedPrescriptionPrintingDialog', false)
      ) {
        return openBaseDialog(
          ({ onClose }) => {
            const onProceed = () => {
              onClose();
              setFieldValue('generatePdf', false);
            };

            return <DialogRetainSignPrescription onProceed={onProceed} onIgnore={() => onIgnore(onClose)} />;
          },
          { width: '615px', persist: true }
        );
      }

      return onIgnore();
    },
    [isDigitalCertificateSelected, openBaseDialog, prescriberConfiguration, setFieldValue]
  );

  const onChangeIncludeDate = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue('showDateInPdf', event.target.checked);
    },
    [setFieldValue]
  );

  const onChangeOpenPatientScreen = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!prescription || !prescriberConfigurations) return;
      setFieldValue('openPatientApp', event.target.checked);

      PrescriberService.updatePrescriberOpenPatientApp(event.target.checked, prescription.Prescritor.idPrescritor);

      if (prescriberConfigurations.Configuracoes === null) return;

      setPrescriberConfigurations({
        ...prescriberConfigurations,
        Configuracoes: { ...prescriberConfigurations.Configuracoes, openPatientApp: event.target.checked },
      });
    },
    [prescriberConfigurations, prescription, setFieldValue, setPrescriberConfigurations]
  );

  const onChangeIssueMedicinesInSeparateTabs = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!prescription || !prescriberConfigurations) return;
      setFieldValue('issueMedicinesInSeparateTabs', event.target.checked);

      PrescriberService.updateIssueMedicinesInSeparateTabs(event.target.checked, prescription.Prescritor.idPrescritor);

      if (prescriberConfigurations.Configuracoes === null) return;

      setPrescriberConfigurations({
        ...prescriberConfigurations,
        Configuracoes: {
          ...prescriberConfigurations.Configuracoes,
          issueMedicinesInSeparateTabs: event.target.checked,
        },
      });
    },
    [prescriberConfigurations, prescription, setFieldValue, setPrescriberConfigurations]
  );

  const onChangeSendPrescriptionWhatsApp = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      safeSetInLocalStorage('shouldSendPrescriptionWhatsApp', String(event.target.checked));
      if (hasWhatsAppConsent) {
        Tracker.trackEvent({
          event: 'SendWhatsappWeb',
        });
        setShouldSendPrescriptionWhatsApp(event.target.checked);
        return;
      }

      setShowWhatsAppDialogConsent(event.target.checked);
      setShouldSendPrescriptionWhatsApp(event.target.checked);
    },
    [setShowWhatsAppDialogConsent, hasWhatsAppConsent]
  );

  const openLocalSignatureDialog = useCallback(() => {
    return openBaseDialog(
      (dialogProps: DialogWrappedComponentProps) => <LocalCertificateFlow onClose={dialogProps.onClose} />,
      {
        width: dialogWidth,
        height: 'auto',
        persist: false,
      }
    );
  }, [dialogWidth, openBaseDialog]);

  const onSelectCertificate = useCallback(
    async (value: CERTIFICATES) => {
      if (!prescription) return;

      Tracker.trackEvent({
        event: 'Type of Signature Selected',
        parameters: {
          'CD Selected': value,
        },
      });

      if (value === CERTIFICATES.LOCAL) return openLocalSignatureDialog();

      const testFlow = prescriberConfigurations?.Configuracoes?.signatureRaffleFlow || '';

      await TrackerEventService.track(prescription.idPrescricao, 'certificates-ab-test', {
        fluxo: testFlow,
        assinaturaDigital: value !== CERTIFICATES.MANUAL,
        idMedico: prescription.idPrescritor,
      });
      setSelectedCertificateType(value);
    },
    [
      openLocalSignatureDialog,
      prescriberConfigurations?.Configuracoes?.signatureRaffleFlow,
      prescription,
      setSelectedCertificateType,
    ]
  );

  const openStandardSignatureDialogWithError = useCallback(() => {
    return openBaseDialog(
      (dialogProps: DialogWrappedComponentProps) => (
        <CertificateList
          onSelect={(val) => {
            if (!val || val === CERTIFICATES.NOT_DEFINED)
              return toast.error('Selecione um tipo de certificado para prosseguir.');

            onSelectCertificate(val);
            dialogProps.onClose();
          }}
          onClose={() => dialogProps.onClose()}
          selectedCertificateType={selectedCertificateType}
          lastCertificateHasError={true}
        />
      ),
      {
        width: dialogWidth,
        height: 'auto',
        persist: false,
      }
    );
  }, [dialogWidth, onSelectCertificate, openBaseDialog, selectedCertificateType]);

  const onClickCancelFooter = useCallback(() => {
    if (smrForm && smrForm.prescricaoCidLme.length > 0) {
      history.push(`${path}/lme`);
      return;
    }

    history.push(path);
  }, [history, path, smrForm]);

  const closeValidatedPrescription = useCallback(
    (data?: { pin?: string } | null) => {
      runWithLoadingClosePrescription(async () => {
        if (!prescription) return;

        const certificateAuthentication = data ? { pin: data.pin } : null;

        const payload: ClosePrescriptionPayload = {
          generatePdf: form.values.generatePdf || selectedCertificateType === CERTIFICATES.MANUAL,
          openPatientApp: form.values.openPatientApp,
          certificateAuthentication,
          prescriptionObservation: prescriptionObservation,
          email: form.values.emissionEmail,
          phone: form.values.emissionPhone,
          certificateType: signInEhr ? null : selectedCertificateType,
          localCertificate: selectedLocalCertificate,
          shouldSendPrescriptionWhatsApp,
          showDateInPdf: disableSelectShowDateInPdf || form.values.showDateInPdf,
          emittedTo,
          issueMedicinesInSeparateTabs: form.values.issueMedicinesInSeparateTabs,
          hasDigitalCertificates: false,
          skipDefaultRqe: selectedRqe === DONT_SHOW_RQE_IN_PDF_KEY,
        };

        payload.patientData = {};

        const getHouseNumber = () => {
          if (houseNumberHasNoNumber) {
            return 'S/N';
          }
          return form.values.patientNumber || '';
        };

        payload.patientData.address = {
          address: form.values.patientAddress || '',
          city: form.values.patientCity || '',
          complement: form.values.patientComplement || '',
          federalUnit: form.values.patientFederalUnit || '',
          neighborhood: form.values.patientNeighborhood || '',
          number: getHouseNumber(),
          postalCode: form.values.patientPostalCode || '',
        };

        if (needToFillGender && payload.patientData) {
          payload.patientData.gender = form.values.patientGender;
        }

        const patientBirthDate = form.values.patientBirthDate;
        if (payload.patientData && patientBirthDate) {
          payload.patientData.birthDate = DateFromFormat(patientBirthDate, 'dd/MM/yyyy');
        }

        if (needToFillIcd && icd && payload.patientData) {
          payload.patientData.icd10structured = [
            {
              code: icd.subcategory,
              idICD10: icd.id,
              description: icd.description,
            },
          ];
        }

        const patientDocument = form.values.patientDocument || prescription?.Paciente?.Documento;
        const patientPassport = form.values.patientPassport;
        const patientCountry = countriesData.find(country => country.id === form.values.patientCountry)?.name;
        const nationality = countriesData.find(country => country.id === form.values.patientCountry)?.nationality;

        if (needToFillDocument) {
          if (!patientWithoutCpf) {
            payload.patientData.document = onlyDigits(patientDocument);
            payload.patientData.passport = undefined;
          }

          else if (showPatientIsForeigner === PATIENT_FOREIGNER.NO_CPF) {
            payload.patientData.document = null;
            payload.patientData.passport = undefined;
          }

          else if (showPatientIsForeigner === PATIENT_FOREIGNER.FOREIGNER) {
            payload.patientData.document = undefined;
            payload.patientData.passport = {
              number: patientPassport,
              country: nationality || '',
              emissionCountry: patientCountry || ''
            }
          }
        }

        if (needToFillEstablishmentAddress) {
          const values = form.values;
          const establishmentAddress = values.establishmentAddress;

          const getHouseNumber = () => {
            if (form.values.establishmentHouseWithoutNumber) {
              return 'S/N';
            }
            return form.values.establishmentNumber || '';
          };

          payload.establishment = {
            address1: `${establishmentAddress} ${getHouseNumber()}`,
            address2: values.establishmentComplement,
            city: values.establishmentCity,
            postalCode: values.establishmentPostalCode,
            state: values.establishmentFederalUnit,
            neighborhood: values.establishmentNeighborhood,
          };
        }

        const { closed, errorMessage, hasCertificateError } = await waitAtLeast(FIVE_SECONDS_IN_MS, () =>
          closePrescription(payload)
        );

        if (hasCertificateError) {
          setLastCertificateHasError(true);
          openStandardSignatureDialogWithError();
          return;
        }

        Tracker.peopleSetOnce('First Finished Prescription Date', new Date());
        Tracker.peopleSet('Last Finished Prescription Date', new Date());

        if (!closed) {
          if (errorMessage) {
            toast.error(errorMessage);
          }
          return;
        }

        Tracker.trackEvent({
          event: 'FinishPrescription',
          parameters: {
            'Prescription Signed': prescription.PrescricaoAssinada,
            'Prescription needs Notification': prescription.NecessitaNotificacaoReceitaPapel,
            'Special Prescription Required': prescription.RequerReceituarioEspecial,
            CertificadoAssinatura: selectedCertificateType,
            Impressao: prescription.PermitirImpressao,
            email: !!get(prescription, 'Paciente.Emails[0]', false),
            Whatsapp: shouldSendPrescriptionWhatsApp,
          },
        });

        const premiumMedicineIds = prescription.Itens.filter((medicine) => medicine.isPremiumPlacement).map(
          (item) => item.IdMedicamento
        );

        if (premiumMedicineIds.length > 0) {
          await TrackerEventService.track(prescription.idPrescricao, 'Receita_emitida_com_item_patrocinado', {
            idPrescricao: prescription.idPrescricao,
            idMedico: prescription.Prescritor.idPrescritor,
            idMedicamento: premiumMedicineIds,
            exibirPorPrincipioAtivo: prescription.ExibirPorPrincipioAtivo,
          });
        }

        const suggestionFromIndustryIds = prescription.Itens.filter(
          (medicine) => medicine.posologySelectedFromSuggestion
        ).map((item) => ({
          idMedicamento: item.IdMedicamento,
          idPosologiaSugerida: item.posologySelectedFromSuggestionId,
        }));

        if (suggestionFromIndustryIds.length > 0) {
          await TrackerEventService.track(prescription.idPrescricao, 'backoffice_campaign_posologia_prescrita', {
            idPrescricao: prescription.idPrescricao,
            idMedico: prescription.Prescritor.idPrescritor,
            idMedicamento: Array.from(new Set(suggestionFromIndustryIds.map((item) => item.idMedicamento))),
            idPosologiaSugerida: Array.from(new Set(suggestionFromIndustryIds.map((item) => item.idPosologiaSugerida))),
            CodigoValidacao: prescription.CodigoValidacao,
            idParceiro: prescription.Origem.idParceiro,
            isMobile,
            TimeStamp: DateTime.now().toISO(),
          });
        }

        setEmitted(true);
      });
    },
    [
      closePrescription,
      disableSelectShowDateInPdf,
      emittedTo,
      form.values,
      houseNumberHasNoNumber,
      icd,
      needToFillDocument,
      needToFillEstablishmentAddress,
      needToFillGender,
      needToFillIcd,
      prescription,
      prescriptionObservation,
      runWithLoadingClosePrescription,
      selectedCertificateType,
      selectedLocalCertificate,
      shouldSendPrescriptionWhatsApp,
      signInEhr,
      openStandardSignatureDialogWithError,
      isMobile,
      selectedRqe,
      showPatientIsForeigner,
      patientWithoutCpf
    ]
  );

  const updatePrescriberDialogPrescriptionNotSigned = useCallback(async () => {
    if (!prescription) return;

    const controlledPrescriptionSigned = {
      value: true,
      lastUpdated: new Date(),
    };

    await PrescriberService.updatePrescriberDialogControlledPrescriptionSigned(
      controlledPrescriptionSigned,
      prescription.Prescritor.idPrescritor
    );
  }, [prescription]);

  const showDialogAskForPrintOrDigitalCertificate = useCallback(() => {
    const handleChangeCertificateType = () => {
      Tracker.trackEvent({
        event: 'GoBackToSelectCertificate',
      });
      window.dispatchEvent(new CustomEvent('go-back-to-select-certificate'));
    };

    setModal(() => (
      <Portal>
        <DialogAskForPrintOrDigitalCertificate
          onPrintAndSign={closeValidatedPrescription}
          onDigitalCertificate={handleChangeCertificateType}
          open={true}
          onClose={() => setModal(null)}
          onNotShowAgain={updatePrescriberDialogPrescriptionNotSigned}
        />
      </Portal>
    ));
    return;
  }, [setModal, closeValidatedPrescription, updatePrescriberDialogPrescriptionNotSigned]);

  const trackEventJustifyPrint = useCallback(
    async (justify: string) => {
      let eventName = '';

      switch (justify) {
        case JustificationOptions.PatientRequest:
          eventName = 'justificativa_impressao_paciente_solicitou';
          break;
        case JustificationOptions.PatientNoConection:
          eventName = 'justificativa_impressao_paciente_sem_internet';
          break;
        case JustificationOptions.MandatoryForm:
          eventName = 'justificativa_impressao_forms_especifico';
          break;
        case JustificationOptions.DigitalCertificateUnavailable:
          eventName = 'justificativa_impressao_instabilidade_cd';
          break;
      }

      if (prescription)
        await TrackerEventService.track(prescription.idPrescricao, eventName, {
          data: DateTime.now().toFormat('dd/MM/yyyy'),
          hora: DateTime.now().toFormat('HH:mm:ss'),
          idParceiro: prescriberConfiguration?.idParceiro,
          subParceiro: prescription.Origem.Nome,
          idSubParceiro: prescription.Origem.SubParceiro,
          idMedico: prescription?.idPrescritor,
          nomeMedico: prescription?.Prescritor.Nome,
          crmMedico: prescription?.Prescritor.Numero,
          idPaciente: prescription?.Paciente.idPaciente,
          idPrescricao: prescription?.idPrescricao,
          codigoValidacao: prescription?.CodigoValidacao,
          token: prescription?.CodigoValidacao,
          certificado: selectedCertificateType,
        });
    },
    [prescription, prescriberConfiguration, selectedCertificateType]
  );

  const onClickContinueFooter = useCallback(async () => {
    if (!prescription) return;

    const validation = [];

    const errors = await form.validateForm();

    const hasErrors = Object.keys(errors).length > 0;
    Object.keys(errors).forEach((key) => {
      const errorKey = key as keyof typeof errors;
      if (errors[errorKey]) {
        form.setFieldTouched(key, true);
        validation.push(false);
      }
    });

    let saveJustificate: string = '';

    if (enablePrintingForms && selectedCertificateType !== CERTIFICATES.MANUAL && form.values.generatePdf) {
      await openBaseDialog(
        (dialogProps: DialogWrappedComponentProps) => {
          return (
            <PrintJustifyDialog
              onClose={() => {
                dialogProps.onClose();
                return;
              }}
              onSubmit={(justificate: JustificationOptions) => {
                saveJustificate = justificate;
                trackEventJustifyPrint(justificate);
                dialogProps.onClose();
              }}
            />
          );
        },
        {
          width: 'auto',
          height: 'auto',
          backgroundColor: 'transparent',
        }
      );
    }

    if (
      enablePrintingForms &&
      selectedCertificateType !== CERTIFICATES.MANUAL &&
      form.values.generatePdf &&
      saveJustificate === ''
    )
      return;

    // Considera disponível a certificadora que o médico possui E que não está instável/indisponível
    const hasAvailableCertifier = ownedCertificates.some(
      (certificate) =>
        certificate.status === PrescriptionCertificateEnum.VALID && !isCertifierUnstable(certificate.certifierStatus)
    );

    // Impede finalizar quando o método selecionado é uma certificadora indisponível (inativa)
    const selectedOwnedCertificate = ownedCertificates.find(
      (certificate) => certificate.name === selectedCertificateType
    );
    if (selectedOwnedCertificate?.certifierStatus === CERTIFIER_STATUS.INACTIVE) {
      toast.error(
        'A certificadora selecionada está indisponível. Selecione outro certificado ou utilize "Assinar e Carimbar" para finalizar.'
      );
      return;
    }

    if (
      enableMandatoryDigitalCertificate &&
      !lastCertificateHasError &&
      (selectedCertificateType === CERTIFICATES.MANUAL ||
        selectedCertificateType === CERTIFICATES.NOT_DEFINED ||
        selectedCertificateType === null)
    ) {
      if (!(selectedCertificateType === CERTIFICATES.MANUAL && !hasAvailableCertifier)) {
        toast.error('Selecione o certificado digital para emitir o(s) documento(s).');
        return;
      }
    }

    if ((selectedCertificateType === CERTIFICATES.NOT_DEFINED || selectedCertificateType === null) && !signInEhr) {
      toast.error('Selecione um método de assinatura para emitir o(s) documento(s).');
      return;
    }

    if (selectedCertificateType === CERTIFICATES.LOCAL) {
      validation.push(localCertificateValidation.validate(selectedLocalCertificate));
    }

    if (hasErrors || validation.some((v) => !v)) {
      scrollTo('Container', {
        top: 0,
      });

      return;
    }

    if (selectedCertificateType === CERTIFICATES.VALID) {
      const userHasVidaas = await runWithLoadingCertificationSignatureSession(async () => {
        try {
          return await CertificateService.vidaas.checkIfUserHasVidaas(prescription.Prescritor.Documento);
        } catch (error) {
          toast.error(
            `O CPF ${formatCpf(prescription.Prescritor.Documento)} não foi encontrado na certificadora Valid.`
          );
        }
      });

      if (!userHasVidaas) return;

      const isUserAuthorized = await runWithLoadingCertificationSignatureSession(async () => {
        try {
          return await CertificateService.vidaas.checkVidaasAuthorization(prescription.Prescritor.Documento);
        } catch (error) { }
      });

      if (!isUserAuthorized) {
        const emittedAuthorization = await runWithLoadingCertificationSignatureSession(async () => {
          try {
            return await CertificateService.vidaas.authorizeVidaas(prescription.Prescritor.Documento, VIDAAS_DAYS_LIFE);
          } catch (error) {
            toast.error(
              get(error, 'response.data.Descricao', 'Não foi possível emitir a autorização do certificado selecionado.')
            );
          }
        });

        if (emittedAuthorization) {
          history.push({
            pathname: `${path}/vidaas-authorize`,
            state: {
              fromEmission: true,
            },
          });
          return;
        }
      }
    }

    if (selectedCertificateType === CERTIFICATES.SAFEWEB) {
      const userHasSafeId = await runWithLoadingCertificationSignatureSession(async () => {
        try {
          return await CertificateService.safeweb.checkIfUserHasSafeid(prescription.Prescritor.Documento);
        } catch (error) {
          toast.error(
            `O CPF ${formatCpf(prescription.Prescritor.Documento)} não foi encontrado na certificadora Safeweb.`
          );
        }
      });

      if (!userHasSafeId) return;

      const isUserAuthorized = await runWithLoadingCertificationSignatureSession(async () => {
        try {
          return await CertificateService.safeweb.checkIfUserAuthorized(prescription.Prescritor.Documento);
        } catch (error) { }
      });

      if (!isUserAuthorized) {
        await CertificateService.safeweb.authorize(
          prescription.Prescritor.Documento,
          DateTime.fromJSDate(new Date()).plus({ day: -1, year: 1 }).toFormat('dd/MM/yyyy')
        );

        history.push({
          pathname: `${path}/safeid-authorize`,
          search: 'source=emission',
        });
        return;
      }

      const session = await runWithLoadingCertificationSignatureSession(async () => {
        try {
          return await CertificateService.safeweb.checkSession(prescription.idPrescricao);
        } catch (error) {
          toast.error('Não foi possível emitir a autorização do certificado selecionado.');
        }
      });

      if (!session) return;

      if (!session.SessaoAtiva) {
        const isAuthenticated = await authenticateSafeweb({
          prescriptionId: prescription.idPrescricao,
          targetId: 'PrescriptionEmission',
        });
        if (!isAuthenticated) {
          return;
        }
      }
    }

    let pin: null | string = null;

    if (selectedCertificateType === CERTIFICATES.EVAL_CONTEXT) {
      let needToFillPin = true;

      needToFillPin = await runWithLoadingCertificationSignatureSession(async () => {
        try {
          return await (
            await CertificateService.madics.checkSession(prescription.idPrescricao)
          ).needToFillPin;
        } catch (error) {
          toast.error('Não foi possível emitir a autorização do certificado selecionado.');
        }
      });

      if (isUndefined(needToFillPin)) return;

      if (needToFillPin) {
        const authenticateEval = () => {
          return new Promise<void>((resolve) => {
            const handleOnConfirm = (val: string) => {
              pin = val;
              resolve();
            };

            const handleCancel = () => {
              resolve();
            };

            setModal(() => (
              <Portal>
                <AuthenticateWithPin
                  onConfirm={handleOnConfirm}
                  onCancel={handleCancel}
                  certificate={CERTIFICATES.EVAL_CONTEXT}
                  passwordLabel={'Senha e-val'}
                />
              </Portal>
            ));
          });
        };

        await authenticateEval();
        setModal(null);

        if (!pin) {
          return;
        }
      }
    }

    if (selectedCertificateType === CERTIFICATES.CERTISIGN) {
      const session = await runWithLoadingCertificationSignatureSession(async () => {
        try {
          return await CertificateService.certiSign.checkSession(prescription.idPrescricao);
        } catch (error) {
          toast.error('Não foi possível emitir a autorização do certificado selecionado.');
        }
      });

      if (!session) return;

      if (!session.TemSessao) {
        const isAuthenticated = await authenticateCertiSign({
          prescriptionId: prescription.idPrescricao,
          targetId: 'PrescriptionEmission',
          needsOTP: session.NecessarioOTP,
        });

        if (!isAuthenticated) return;
      }
    }

    if (selectedCertificateType === CERTIFICATES.DINAMO_UNIMEDBH) {
      const authenticateDinamoUnimedBH = () => {
        return new Promise<void>((resolve) => {
          const handleOnConfirm = (val: string) => {
            pin = val;
            resolve();
          };

          const handleCancel = () => {
            resolve();
          };

          setModal(() => (
            <Portal>
              <AuthenticateWithPin
                onConfirm={handleOnConfirm}
                onCancel={handleCancel}
                certificate={CERTIFICATES.DINAMO_UNIMEDBH}
                passwordLabel={'Senha do certificado'}
              />
            </Portal>
          ));
        });
      };

      await authenticateDinamoUnimedBH();
      setModal(null);

      if (!pin) {
        return;
      }
    }

    if (selectedCertificateType === CERTIFICATES.DINAMO) {
      const isUserAuthorized = await runWithLoadingCertificationSignatureSession(async () => {
        try {
          return await CertificateService.dinamo.checkSession(prescription.idPrescricao);
        } catch (error) {
          toast.error('Não foi possível emitir a autorização do certificado selecionado.');
          return;
        }
      });

      if (isUndefined(isUserAuthorized)) return;

      if (!isUserAuthorized) {
        const isAuthenticated = await authenticateDinamo({
          prescriptionId: prescription.idPrescricao,
          targetId: 'PrescriptionEmission',
        });

        if (!isAuthenticated) return;
      }
    }

    if (selectedCertificateType === CERTIFICATES.REMOTE_ID) {
      const session = await runWithLoadingCertificationSignatureSession(
        async (): Promise<RemoteIDSessionData | undefined> => {
          try {
            return await CertificateService.remoteId.checkSession(prescription.idPrescricao);
          } catch (error) {
            toast.error('Não foi possível emitir a autorização do certificado selecionado.');
          }
        }
      );

      if (!session) return;

      if (!session.TemSessao) {
        const isAuthenticated = await authenticateRemoteId({
          prescriptionId: prescription.idPrescricao,
          targetId: 'PrescriptionEmission',
          needsOTP: true,
        });

        if (!isAuthenticated) return;
      }
    }

    if (!prescriberConfigurations) {
      return toast.error('Falha ao carregar informações do prescritor. Inicie uma nova receita.');
    }

    if (
      doesPrescriptionHasSigningWarning &&
      !get(prescriberConfigurations, `Configuracoes.dialogs.controlledPrescriptionSigned.value`)
    ) {
      showDialogAskForPrintOrDigitalCertificate();
      return;
    }

    const authentication = pin ? { pin } : null;

    const signedDialogs: Dialog | null = get(prescriberConfigurations, 'Configuracoes.dialogs', null);

    if (doesPrescriptionHasSigningWarning && signedDialogs) {
      const controlledPrescriptionSigned: DialogContent | null = get(
        signedDialogs,
        PRESCRIBER_DIALOG_TYPES.controlledPrescriptionSigned,
        null
      );

      const shouldSeeDialog = !get(controlledPrescriptionSigned, 'value', false);

      if (shouldSeeDialog) {
        showDialogAskForPrintOrDigitalCertificate();
        return;
      }

      Tracker.trackEvent({
        event: 'SignAndStamp',
        parameters: {
          blockAlert: shouldSeeDialog,
        },
      });

      closeValidatedPrescription(authentication);
      return;
    }

    closeValidatedPrescription(authentication);
  }, [
    prescription,
    form,
    enableMandatoryDigitalCertificate,
    selectedCertificateType,
    signInEhr,
    prescriberConfigurations,
    doesPrescriptionHasSigningWarning,
    closeValidatedPrescription,
    localCertificateValidation,
    ownedCertificates,
    selectedLocalCertificate,
    runWithLoadingCertificationSignatureSession,
    history,
    path,
    showDialogAskForPrintOrDigitalCertificate,
    openBaseDialog,
    trackEventJustifyPrint,
    enablePrintingForms,
    lastCertificateHasError,
  ]);

  const onFindAddress = (address: Address) => {
    setFieldValue('patientCity', address.localidade, false);
    setFieldValue('patientNeighborhood', address.bairro, false);
    setFieldValue('patientFederalUnit', address.uf, false);
    setFieldValue('patientAddress', address.logradouro, false);
  };

  const handlePostalCodeInput = useCallback(
    (value: string) => {
      setFieldValue('patientPostalCode', value);
    },
    [setFieldValue]
  );

  const onChangeGenre = useCallback(
    (value: string) => {
      setFieldValue('patientGender', value, false);
      setTimeout(() => {
        validateField('patientGender');
      }, 1);
    },
    [setFieldValue, validateField]
  );

  const continueLabel = useMemo(() => {
    if (!!prescriberConfiguration?.HabilitarSalvarSemEnviar) return 'Salvar';

    return isMobile ? 'Enviar' : 'Assinar e enviar';
  }, [isMobile, prescriberConfiguration?.HabilitarSalvarSemEnviar]);

  const EmissionDateSwitch = useMemo(() => {
    if (disableSelectShowDateInPdf) {
      return null;
    }

    return (
      <FormControl display="flex" alignItems="center">
        <Switch isChecked={form.values.showDateInPdf} onChange={onChangeIncludeDate} id="emissionDate" mr={'10px'} />
        <FormLabel htmlFor="emissionDate" mb="0" fontSize={'12px'} lineHeight={'13px'} cursor={'pointer'}>
          Incluir data na receita
        </FormLabel>
      </FormControl>
    );
  }, [disableSelectShowDateInPdf, form.values.showDateInPdf, onChangeIncludeDate]);

  const PatientScreenSwitch = useMemo(() => {
    if (disablePatientAppSwitch) {
      return null;
    }

    return (
      <FormControl display="flex" alignItems="center">
        <Switch
          isChecked={form.values.openPatientApp}
          onChange={onChangeOpenPatientScreen}
          id="openPatientAppSwitch"
          mr={'10px'}
          isDisabled={selectedCertificateType === CERTIFICATES.MANUAL}
        />
        <FormLabel htmlFor="openPatientAppSwitch" mb="0" fontSize={'12px'} lineHeight={'13px'} cursor={'pointer'}>
          Abrir link enviado ao paciente
        </FormLabel>
      </FormControl>
    );
  }, [disablePatientAppSwitch, form.values.openPatientApp, onChangeOpenPatientScreen, selectedCertificateType]);

  const IssueMedicinesInSeparateTabsSwitch = useMemo(() => {
    if (!enableIssueMedicinesInSeparateTabsSwitch) {
      return null;
    }

    return (
      <FormControl display="flex" alignItems="center">
        <Switch
          isChecked={form.values.issueMedicinesInSeparateTabs}
          onChange={onChangeIssueMedicinesInSeparateTabs}
          id="issueMedicinesInSeparateTabs"
          mr={'10px'}
        />
        <FormLabel
          htmlFor="issueMedicinesInSeparateTabs"
          mb="0"
          fontSize={'12px'}
          lineHeight={'13px'}
          cursor={'pointer'}
        >
          Emitir medicamentos em páginas separadas
        </FormLabel>
      </FormControl>
    );
  }, [
    enableIssueMedicinesInSeparateTabsSwitch,
    form.values.issueMedicinesInSeparateTabs,
    onChangeIssueMedicinesInSeparateTabs,
  ]);

  const PrintSwitch = useMemo(() => {
    if (!showPrintOption) return;

    return (
      <FormControl display="flex" alignItems="center" isDisabled={doesPrescriptionHasLME}>
        <Switch isChecked={form.values.generatePdf} onChange={onChangeGeneratePdf} id="checkGeneratePdf" mr={'10px'} />
        <FormLabel
          htmlFor="checkGeneratePdf"
          mb={'0'}
          data-cy={'checkGeneratePdf'}
          fontSize={'12px'}
          lineHeight={'13px'}
          cursor={'pointer'}
        >
          Imprimir
        </FormLabel>
      </FormControl>
    );
  }, [form.values.generatePdf, onChangeGeneratePdf, showPrintOption, doesPrescriptionHasLME]);

  const WhatsappSwitch = useMemo(() => {
    const blockWhatsappWeb = get(prescriberConfiguration, 'Features.blockWhatsAppWeb', true);

    if (blockWhatsappWeb) return null;

    return (
      <Flex alignItems={'center'}>
        <FormControl display={'flex'} alignItems={'center'} p={'10px 0'}>
          <Switch
            isChecked={shouldSendPrescriptionWhatsApp}
            onChange={onChangeSendPrescriptionWhatsApp}
            id="checkWhatsapp"
            mr={'5px'}
          />
          <WhatsAppIcon />
          <FormLabel htmlFor={'checkWhatsapp'} mb={'0'} ml={'5px'} data-cy={'checkWhatsapp'} cursor={'pointer'}>
            Enviar por Whatsapp pessoal
          </FormLabel>
        </FormControl>
      </Flex>
    );
  }, [shouldSendPrescriptionWhatsApp, onChangeSendPrescriptionWhatsApp, prescriberConfiguration]);

  const onClickLMEBadge = useCallback(() => {
    history.push(`${path}/lme`);
  }, [history, path]);

  const onClickApacBpaPadge = useCallback(() => {
    history.push(`${path}/apac-bpa`);
  }, [history, path]);

  const onConsentWhatsApp = useCallback(async () => {
    const consent = document.getElementById('whatsappconsent')!.textContent?.trim();

    if (!prescription || !prescription.Prescritor || !consent) return;

    await PrescriptionService.updateWhatsAppConsent(
      prescription.Prescritor.idPrescritor,
      prescription.idPrescricao,
      consent
    );
    Tracker.trackEvent({
      event: 'SendWhatsappWeb',
    });
    setShouldSendPrescriptionWhatsApp(true);
    safeSetInLocalStorage('shouldSendPrescriptionWhatsApp', 'true');
    setShowWhatsAppDialogConsent(false);
    setHasWhatsAppConsent(true);
  }, [prescription]);

  const PrescriptionSignedNotificationCard = useMemo(() => {
    if (!prescriberConfiguration) return;

    return (
      <PrescriptionSignedNotification
        isDigitalCertificateSelected={isDigitalCertificateSelected}
        doesPrescriptionHasSigningWarning={doesPrescriptionHasSigningWarning}
      />
    );
  }, [prescriberConfiguration, doesPrescriptionHasSigningWarning, isDigitalCertificateSelected]);

  useEffect(() => {
    const run = async () => {
      if (!prescription || !prescription.Prescritor) return;
      try {
        const data = await PrescriptionService.fetchWhatsAppConsent(prescription.Prescritor.idPrescritor);
        setHasWhatsAppConsent(data.TermoAceite);
        if (data.TermoAceite) {
          setShouldSendPrescriptionWhatsApp(safeGetFromLocalStorage('shouldSendPrescriptionWhatsApp') === 'true');
          return;
        }
      } catch (e) {
        logger.error(e);
      }
    };

    run();
  }, [prescription, setHasWhatsAppConsent, setShouldSendPrescriptionWhatsApp]);

  const onChangeFederationUnit = useCallback(
    (value: string) => {
      setFieldValue('patientFederalUnit', value);
    },
    [setFieldValue]
  );

  const openJustifiedInteractionsDialog = useCallback(async () => {
    if (!prescription) return;

    const interactions = await PrescriptionService.getInteractionsToJustify(prescription.idPrescricao);

    if (partnerJustifyInteraction && !isEmpty(interactions)) {
      setJustifiedInteractions(interactions);

      await openBaseDialog(
        (dialogProps: DialogWrappedComponentProps) => {
          const onClose = () => {
            dialogProps.onClose();
          };

          const onComplete = () => {
            dialogProps.onClose();
          };

          return <JustifyInteractionsDialog onComplete={onComplete} interactions={interactions} onClose={onClose} />;
        },
        {
          width: isMobile ? '95vw' : '600px',
          height: '450px',
          backgroundColor: 'transparent',
        }
      );
    }
  }, [prescription, openBaseDialog, partnerJustifyInteraction, setJustifiedInteractions, isMobile]);

  const JustifiedInteractionsCard = useMemo(() => {
    if (partnerJustifyInteraction && !isEmpty(justifiedInteractions)) {
      return <InteractionBadge onClick={openJustifiedInteractionsDialog} />;
    }
  }, [partnerJustifyInteraction, justifiedInteractions, openJustifiedInteractionsDialog]);

  const LmeFormCard = useMemo(() => {
    if (smrForm !== null) {
      return <LMEBadge onClick={onClickLMEBadge} />;
    }
  }, [onClickLMEBadge, smrForm]);

  const ApacBpaCard = useMemo(() => {
    if (!!completedForm) {
      return <ApacBpaBadge onClick={onClickApacBpaPadge} />;
    }
  }, [completedForm, onClickApacBpaPadge]);

  const handleChangeHouseNumberHasNoNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('patientNeedToFillNumber', !event.target.checked, false);
    setHouseNumberHasNoNumber(event.target.checked);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    validateField(event.target.name);
  };

  const setIcd = (icd: Icd) => {
    form.setFieldValue('icd', icd);
  };

  const openAnvisaNorma = () => {
    window.open(MEVO_ANVISA_URL, '_blank');
  };

  useEffect(() => {
    if (!prescription?.idPrescricao || !enableDuplicatedPrescriptionAlert) return;

    const run = async () => {
      const data = await PrescriptionService.verifyIfPrescriptionIsDuplicated(prescription.idPrescricao);

      if (
        !data.Duplicado ||
        prescription.idPrescricaoStatus === PRESCRIPTION_STATUS.CANCELED ||
        prescription.idPrescricaoStatus === PRESCRIPTION_STATUS.EMITTED
      )
        return;

      return openBaseDialog(
        ({ onClose }) => {
          return (
            <DialogDuplicatedPrescription
              onCancelPrevious={async () => {
                if (!data.IDPrescricaoDuplicada) return;
                await PrescriptionService.cancelPrescription(data.IDPrescricaoDuplicada, '');
                onClose();
                toast.success('Receita Anterior Cancelada');

                await TrackerEventService.track(prescription.idPrescricao, 'Cancelar receita duplicada');
              }}
              onChangeDate={async () => {
                await TrackerEventService.track(prescription.idPrescricao, 'Receita para data futura');
                onClose();
                setOpenChangeDate(true);
              }}
            />
          );
        },
        { width: '500px', height: 'auto', persist: false }
      );
    };

    run();
  }, [openBaseDialog, prescription?.idPrescricao, prescription?.idPrescricaoStatus, enableDuplicatedPrescriptionAlert]);

  const handleChangeForeignerRadio = (value: PATIENT_FOREIGNER) => {
    setShowPatientIsForeigner(value)
    setFieldValue('patientIsForeigner', value === PATIENT_FOREIGNER.FOREIGNER, false)
  }

  return (
    <>
      <PrescriptionLayout
        loading={loadingCertificationSignatureSession || loadingPrescriptionValidation || loadingPrescriberCoreData}
        showMyCentral={false}
        continueButtonLabel={continueLabel}
        onCancel={onClickCancelFooter}
        onContinue={onClickContinueFooter}
        showMenu={true}
        cancelButtonLabel={'Voltar'}
        hideHeader={!isMobile}
      >
        <Flex
          padding={'30px'}
          display={'flex'}
          flexDirection={{ base: 'column', md: 'row' }}
          gap={'20px'}
          justifyContent={'space-between'}
          id="PrescriptionEmission"
          flex={1}
        >
          <>{Modal}</>
          <PrescriptionEmissionLoading
            emitted={emitted}
            isLoading={loadingClosePrescription}
            enableDialogCloseWindowTab={enableDialogCloseWindowTab}
          />
          <Flex flex={3} flexDirection={'column'}>
            {!isMobile && showMevoThesisBanner && (
              <Flex mb={'20px'} onClick={openAnvisaNorma} cursor={'pointer'}>
                <img style={{ borderRadius: '10px' }} src={BannerMevoNotification} alt={'banner mevo'} />
              </Flex>
            )}
            <PatientEstablishmentCard patientName={patientName} establishment={prescription?.Estabelecimento} />
            {(needToFillAddress || needToFillGender || needToFillBirthdate || needToFillIcd || needToFillDocument) && (
              <Box>
                <Text fontSize={'16px'} fontWeight={'600'}>
                  Informações faltantes do paciente
                </Text>
                <Flex alignItems={'flex-start'}>
                  <Box mt={'4px'}>
                    <FaExclamationCircle
                      color={themeColors.info.main}
                      style={{ marginRight: 4, width: 12, height: 12 }}
                    />
                  </Box>
                  <Box>
                    {needToFillDocument && (
                      <Text fontSize={'12px'} color={'#868E96'} fontWeight={500} mb={'10px'}>
                        O documento do paciente é um requisito regulatório da RDC 1000/2025 da Anvisa.
                      </Text>
                    )}
                  </Box>
                </Flex>
              </Box>
            )}

            <Box>
              <Flex flexDir={{ base: 'column', md: 'row' }} w={'100%'} gap={'10px'} mt={'10px'}>
                {needToFillDocument && (
                  <FormControl
                    isInvalid={!!form.errors.patientDocument}
                    isRequired={hasAnyPortaria344 && enableMandatoryCpfPortaria344 && !patientWithoutCpf}
                  >
                    <FormLabel>CPF do paciente</FormLabel>
                    <MaskedInput
                      w={'100%'}
                      value={form.values.patientDocument}
                      name={'patientDocument'}
                      onChange={form.handleChange}
                      placeholder={'000.000.000-00'}
                      mask={CHAKRA_UI_MASKS.CPF}
                      data-testid={'input-document'}
                      onBlur={handleBlur}
                      maskChar={''}
                      isDisabled={patientWithoutCpf}
                    />
                    <FormErrorMessage>{form.errors.patientDocument}</FormErrorMessage>
                    <Flex mt={'16px'}>
                      <Checkbox
                        size={'md'}
                        isChecked={patientWithoutCpf}
                        onChange={(e) => {
                          setPatientWithoutCpf(e?.target?.checked)
                          setShowPatientIsForeigner(e?.target?.checked ? PATIENT_FOREIGNER.NO_CPF : null)
                          setFieldValue('patientIsForeigner', false, false)
                          setFieldValue('patientWithoutCpf', e?.target?.checked, true)
                          if (e?.target?.checked) {
                            setFieldValue('patientDocument', '', false)
                          }
                        }

                        }
                      >
                        Paciente sem CPF
                      </Checkbox>
                    </Flex>
                  </FormControl>
                )}
                {needToFillBirthdate && (
                  <FormControl isInvalid={!!form.errors.patientBirthDate}>
                    <FormLabel>Data de nascimento*</FormLabel>
                    <MaskedInput
                      w={'100%'}
                      value={form.values.patientBirthDate}
                      name={'patientBirthDate'}
                      onChange={form.handleChange}
                      placeholder={'00/00/0000'}
                      mask={CHAKRA_UI_MASKS.BIRTH_DATE}
                      maskChar={''}
                      onBlur={handleBlur}
                      isDisabled={patientWithoutCpf && showPatientIsForeigner === PATIENT_FOREIGNER.FOREIGNER}
                    />
                    <FormErrorMessage>{form.errors.patientBirthDate}</FormErrorMessage>
                  </FormControl>
                )}
              </Flex>

              {patientWithoutCpf && (
                <Flex flexDirection={'column'}>
                  <RadioGroup
                    w={'100%'}
                    onChange={handleChangeForeignerRadio}
                    value={String(showPatientIsForeigner)}
                    size={'sm'}
                  >
                    <Box padding={'12px 16px'} mt={'12px'} border={showPatientIsForeigner === PATIENT_FOREIGNER.NO_CPF ? '1px solid #8A6AB8' : '1px solid #DEE2E6'} backgroundColor={showPatientIsForeigner === PATIENT_FOREIGNER.NO_CPF ? '#F5F5FF' : '#fff'} borderRadius={'8px'} width={'100%'}>
                      <Radio value={PATIENT_FOREIGNER.NO_CPF}>
                        <Text fontSize="12px" color="gray.900" fontWeight={700}>
                          Paciente brasileiro sem CPF
                        </Text>
                      </Radio>
                      {showPatientIsForeigner === PATIENT_FOREIGNER.NO_CPF && (
                        <>
                          <Divider mt={'4px'} mb={'4px'} />
                          {isMobile ? (
                            <Text fontSize="12px" color="gray.900" fontWeight={500}>
                              Declaro que o paciente afirma que não possui/sabe o CPF{' '}
                              <DoubtIcon style={{ display: 'inline', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => setShowCpfTooltipMobile(true)} />
                            </Text>
                          ) : (
                            <Flex gap={'4px'}>
                              <Text fontSize="12px" color="gray.900" fontWeight={500}>
                                Declaro que o paciente afirma que não possui/sabe o CPF
                              </Text>
                              <Tooltip
                                label="De acordo com a Anvisa, o CPF/Passaporte são obrigatórios. A emissão sem o documento aplica-se apenas em caráter excepcional, quando o paciente não o possui."
                                placement="bottom"
                                hasArrow
                                bg="primary.main"
                                color="white"
                                borderRadius="8px"
                                fontSize="14px"
                                p={2}
                              >
                                <DoubtIcon onClick={() => setShowCpfTooltipMobile(true)} />
                              </Tooltip>
                            </Flex>
                          )}
                        </>
                      )}
                    </Box>
                    <Box padding={'12px 16px'} mt={'8px'} border={showPatientIsForeigner === PATIENT_FOREIGNER.FOREIGNER ? '1px solid #8A6AB8' : '1px solid #DEE2E6'} backgroundColor={showPatientIsForeigner === PATIENT_FOREIGNER.FOREIGNER ? '#F5F5FF' : '#fff'} borderRadius={'8px'} width={'100%'}>
                      <Radio value={PATIENT_FOREIGNER.FOREIGNER}>
                        <Text fontSize="12px" color="gray.900" fontWeight={700}>
                          Paciente estrageiro
                        </Text>
                      </Radio>
                      {showPatientIsForeigner === PATIENT_FOREIGNER.FOREIGNER && (
                        <Grid gridTemplateColumns={isMobile ? '1fr' : '1fr 1fr'} gap={'24px'} mt={'4px'}>
                          <FormControl
                            isInvalid={!!form.errors.patientPassport}
                            isRequired={showPatientIsForeigner === PATIENT_FOREIGNER.FOREIGNER}
                          >
                            <FormLabel>Passaporte</FormLabel>
                            <Input
                              value={form.values.patientPassport}
                              name={'patientPassport'}
                              onChange={form.handleChange}
                              placeholder={'Passaporte'}
                              onBlur={handleBlur}
                              border={'none'}
                            />
                          </FormControl>
                          <FormControl isInvalid={!!form.errors.patientBirthDate}>
                            <FormLabel>Data de nascimento*</FormLabel>
                            <MaskedInput
                              value={form.values.patientBirthDate}
                              name={'patientBirthDate'}
                              onChange={form.handleChange}
                              placeholder={'00/00/0000'}
                              mask={CHAKRA_UI_MASKS.BIRTH_DATE}
                              maskChar={''}
                              onBlur={handleBlur}
                              border={'none'}
                            />
                            <FormErrorMessage>{form.errors.patientBirthDate}</FormErrorMessage>
                          </FormControl>
                          <FormControl
                            isInvalid={!!form.errors.patientCountry}
                            isRequired={showPatientIsForeigner === PATIENT_FOREIGNER.FOREIGNER}
                          >
                            <FormLabel>País de emissão</FormLabel>
                            <SelectCountryAutocomplete
                              value={form.values.patientCountry}
                              onChange={(value) => {
                                form.setFieldValue('patientCountry', value);
                                form.setFieldTouched('patientCountry', true);
                              }}
                              error={!!form.errors.patientCountry && !!form.touched.patientCountry}
                            />
                            <FormErrorMessage>{form.errors.patientCountry}</FormErrorMessage>
                          </FormControl>
                        </Grid>
                      )}
                    </Box>
                  </RadioGroup>
                </Flex>
              )}

              <Flex flexDir={{ base: 'column', md: 'row' }} w={'100%'} gap={'10px'} mt={'10px'}>
                {needToFillGender && (
                  <FormControl flex={1}>
                    <SelectGender
                      value={form.values.patientGender}
                      onChange={onChangeGenre}
                      size={'medium'}
                      error={!!form.errors.patientGender}
                      isRequired
                    />
                  </FormControl>
                )}

                {needToFillIcd && (
                  <Box flex={needToFillGender ? 3 : 1}>
                    <SearchIcd icd={icd} setIcd={setIcd} hasError={!!form.errors.icd} label={'Diagnóstico (CID)*'} />
                  </Box>
                )}
              </Flex>

              {needToFillAddress && (
                <Flex flexDir={{ base: 'column', md: 'row' }} w={'100%'} gap={'10px'} mt={'10px'}>
                  <Box flex={1}>
                    <SearchAddressByCep
                      initialValue={form.values.patientPostalCode}
                      name={'patientPostalCode'}
                      onFindAddress={onFindAddress}
                      onChange={handlePostalCodeInput}
                      error={!!form.errors.patientPostalCode}
                      size={'medium'}
                      required
                      fullWidth
                      onBlur={handleBlur}
                    />
                  </Box>

                  <FormControl isInvalid={!!form.errors.patientAddress} flex={1}>
                    <FormLabel>Endereço*</FormLabel>
                    <Input
                      w={'100%'}
                      value={form.values.patientAddress}
                      name={'patientAddress'}
                      onChange={form.handleChange}
                      placeholder={'Endereço'}
                      onBlur={handleBlur}
                    />
                  </FormControl>

                  <FormControl isInvalid={!!form.errors.patientNumber} flex={1}>
                    <FormLabel>Número*</FormLabel>
                    <MaskedInput
                      mask={MASKS.NUMBER_FIVE_DIGITS}
                      maskChar={''}
                      w={'100%'}
                      value={form.values.patientNumber}
                      name={'patientNumber'}
                      onChange={form.handleChange}
                      placeholder={'Número'}
                      isDisabled={houseNumberHasNoNumber}
                      onBlur={handleBlur}
                    />
                    <Flex mt={'6px'}>
                      <Checkbox
                        size={'sm'}
                        isChecked={houseNumberHasNoNumber}
                        onChange={handleChangeHouseNumberHasNoNumber}
                      >
                        Sem Número
                      </Checkbox>
                    </Flex>
                  </FormControl>

                  <FormControl flex={1}>
                    <FormLabel>Complemento</FormLabel>
                    <Input
                      w={'100%'}
                      placeholder={'Complemento'}
                      name={'patientComplement'}
                      value={form.values.patientComplement}
                      onChange={form.handleChange}
                      onBlur={handleBlur}
                    />
                  </FormControl>
                </Flex>
              )}

              {needToFillAddress && (
                <Flex flexDir={{ base: 'column', md: 'row' }} w={'100%'} gap={'10px'} mt={'10px'}>
                  <FormControl isInvalid={!!form.errors.patientNeighborhood} flex={1}>
                    <FormLabel>Bairro*</FormLabel>
                    <Input
                      w={'100%'}
                      name="patientNeighborhood"
                      value={form.values.patientNeighborhood}
                      onChange={form.handleChange}
                      placeholder={'Bairro'}
                      onBlur={handleBlur}
                    />
                  </FormControl>

                  <FormControl isInvalid={!!form.errors.patientCity} flex={1}>
                    <FormLabel>Cidade*</FormLabel>
                    <Input
                      w={'100%'}
                      value={form.values.patientCity}
                      name={'patientCity'}
                      onChange={form.handleChange}
                      placeholder={'Cidade'}
                      onBlur={handleBlur}
                    />
                  </FormControl>

                  <FormControl isInvalid={!!form.errors.patientFederalUnit} flex={1}>
                    <SelectState
                      onChange={onChangeFederationUnit}
                      value={form.values.patientFederalUnit}
                      error={!!form.errors.patientFederalUnit}
                      required
                      onBlur={() => validateField('patientFederalUnit')}
                    />
                  </FormControl>
                </Flex>
              )}
            </Box>

            {needToFillAddress && (
              <Box mt={'40px'} mb={'20px'}>
                <Divider />
              </Box>
            )}

            {needToFillEstablishmentAddress ? (
              <>
                <Box>
                  <EstablishmentAddressForm />
                </Box>

                <Box mt={'40px'} mb={'20px'}>
                  <Divider />
                </Box>
              </>
            ) : null}

            {(enableChangePrescriptionEmissionDate || openChangeDate) && (
              <Flex my={4} gap={2} flexDirection={'column'}>
                <Text fontWeight={600} fontSize={'16px'}>
                  Data da Receita:{' '}
                </Text>
                <Box w={{ base: '100%', md: '172px' }}>
                  <Anchor
                    anchorName={ANCHORS.CHANGE_PRESCRIPTION_EMISSION_DATE}
                    position={isMobile ? ANCHOR_POS.BOTTOM : ANCHOR_POS.RIGHT}
                  >
                    <DatePicker
                      popperPlacement={isMobile ? 'bottom' : 'right-end'}
                      onChange={(date: Date) => setEmittedTo(date ?? new Date())}
                      selected={emittedTo}
                      disabled={false}
                      open={openChangeDate}
                      onClickOutside={() => setOpenChangeDate(false)}
                      onInputClick={() => setOpenChangeDate(true)}
                    />
                  </Anchor>
                </Box>
              </Flex>
            )}

            {!prescriberConfiguration?.Features?.hidePatientContactInfo && (
              <>
                <Box mt={'8px'}>
                  <Text fontWeight={600} fontSize={'16px'}>
                    Escolha como enviar a receita para o paciente
                  </Text>
                  <PrescriptionSendingMethodInformative
                    disableWhatsapp={disableWhatsapp}
                    sendSMS={sendSMS}
                    disableEmail={disableEmail}
                    blockWhatsappWeb={blockWhatsappWeb}
                  />
                </Box>

                <Flex
                  flexDirection={{ base: 'column', md: 'row' }}
                  mt={'10px'}
                  gap={'10px'}
                  maxW={{ base: '100%', md: '500px' }}
                >
                  <PhoneInput
                    labelText="CELULAR*"
                    value={form.values.emissionPhone}
                    onChange={(value) => {
                      form.setFieldValue('emissionPhone', value || '');
                    }}
                    onBlur={() => {
                      form.validateField('emissionPhone');
                      Tracker.trackEvent({
                        event: 'Mobile Changed',
                      });
                    }}
                    errorText={form.errors.emissionPhone}
                    isInvalid={!!form.errors.emissionPhone}
                    placeholder="Digite o telefone"
                    defaultCountry="BR"
                    inputTestId="emission-phone-input"
                  />

                  {!disableEmail && (
                    <FormControl isInvalid={!!form.errors.emissionEmail}>
                      <FormLabel>Email</FormLabel>
                      <Anchor
                        anchorName={ANCHORS.EMISSION_EMAIL}
                        position={isMobile ? ANCHOR_POS.BOTTOM : ANCHOR_POS.RIGHT}
                      >
                        <Input
                          w={'100%'}
                          value={form.values.emissionEmail}
                          name={'emissionEmail'}
                          onChange={form.handleChange}
                          placeholder={'Email'}
                          data-cy={'prescriptionEmission-email'}
                        />
                      </Anchor>
                      <FormErrorMessage>{form.errors.emissionEmail}</FormErrorMessage>
                    </FormControl>
                  )}
                </Flex>
              </>
            )}

            <Box maxW={{ base: '100%', md: '500px' }}>
              <WhatsAppDialogConsent
                showWhatsAppDialogConsent={showWhatsAppDialogConsent}
                onCancel={() => {
                  setShouldSendPrescriptionWhatsApp(false);
                  safeSetInLocalStorage('shouldSendPrescriptionWhatsApp', 'false');
                  setShowWhatsAppDialogConsent(false);
                  setHasWhatsAppConsent(false);
                }}
                onConfirm={onConsentWhatsApp}
              />
              {WhatsappSwitch}
            </Box>
            <Flex
              gap={'10px'}
              paddingBottom={'15px'}
              display={{ base: 'none', md: 'flex' }}
              w={'60%'}
              mt={'15px'}
              flexFlow={'nowrap'}
            >
              {LmeFormCard}
              {ApacBpaCard}
              {JustifiedInteractionsCard}
            </Flex>
          </Flex>
          <Flex flex={1} minW={'35%'}>
            <Box
              w={'100%'}
              justifyContent={'flex-end'}
              bg={'gray.lightest'}
              p={'10px'}
              borderRadius={'20px'}
              maxH={'90vh'}
            >
              {PrescriptionSignedNotificationCard}

              <Box mt="10px">
                {!shouldHideCertificates && (
                  <Anchor
                    anchorName={ANCHORS.CHANGE_CERTIFICATE}
                    position={isMobile ? ANCHOR_POS.BOTTOM : ANCHOR_POS.LEFT}
                  >
                    <PrescriptionSigningFlows onSelectCertificate={onSelectCertificate} />
                  </Anchor>
                )}
              </Box>

              <Accordion defaultIndex={0} allowToggle>
                {enablePrescriptionSummary && (
                  <Anchor
                    anchorName={ANCHORS.PRESCRIPTION_SUMMARY}
                    position={isMobile ? ANCHOR_POS.TOP : ANCHOR_POS.LEFT}
                  >
                    <Box mt="10px">
                      <PrescriptionSummary />
                    </Box>
                  </Anchor>
                )}

                <Box bg={'#fff'} borderRadius={'20px'} mt={'10px'}>
                  <Box bg={'#fff'} borderRadius={'17px'} mt={'10px'}>
                    <Box>
                      {enableCustomPdfLayout && (
                        <AccordionItem border="none">
                          <h2>
                            <AccordionButton
                              p={'15px 20px'}
                              justifyContent="space-between"
                              borderRadius="10px"
                              _hover={{}}
                            >
                              <Text fontSize={'14px'} lineHeight={'16px'} fontWeight={700}>
                                Layout do documento
                              </Text>
                              <AccordionIcon />
                            </AccordionButton>
                          </h2>
                          <AccordionPanel p="0 15px">
                            <PDFLayoutList
                              isWidget={true}
                              onCreate={(layoutType: 'SIMPLES' | 'CONTROLADO') =>
                                setCreateEditLayout({
                                  actionType: LAYOUT_ACTION_TYPES.CREATE,
                                  layout: null,
                                  layoutType,
                                })
                              }
                            />
                            <BaseDialog
                              isOpen={!isEmpty(createEditLayout)}
                              persist={false}
                              onClose={() => setCreateEditLayout(null)}
                              width={'95vw'}
                              stylesCustom={{
                                backgroundColor: 'transparent',
                                height: '95vh',
                                boxShadow: 'none',
                              }}
                            >
                              <PDFLayoutEditor
                                goBack={() => {
                                  setCreateEditLayout(null);
                                }}
                                layout={get(createEditLayout, 'layout', null)}
                                actionType={get(createEditLayout, 'actionType', LAYOUT_ACTION_TYPES.EDIT)}
                                layoutType={get(createEditLayout, 'layoutType', 'SIMPLES')}
                              />
                            </BaseDialog>
                            <PDFPreview isSigned={isDigitalCertificateSelected} />
                          </AccordionPanel>
                        </AccordionItem>
                      )}
                      <Box
                        p={`${enableCustomPdfLayout ? 0 : '10px'} 17px 19px`}
                        display={'flex'}
                        flexDirection={'column'}
                        gap={'10px'}
                      >
                        <Flex>
                          {PrintSwitch}
                          {EmissionDateSwitch}
                        </Flex>

                        {PatientScreenSwitch}
                        {IssueMedicinesInSeparateTabsSwitch}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Accordion>
            </Box>
          </Flex>
        </Flex >
      </PrescriptionLayout >
      {showCpfTooltipMobile && (
        <MobileDefaultBottom
          title="Paciente brasileiro sem CPF"
          subtitle="De acordo com a Anvisa, o CPF/Passaporte são obrigatórios. A emissão sem o documento aplica-se apenas em caráter excepcional, quando o paciente não o possui."
          confirmLabel="Entendi"
          onConfirm={() => setShowCpfTooltipMobile(false)}
          onClose={() => setShowCpfTooltipMobile(false)}
          isOpen={showCpfTooltipMobile}
        />
      )}
    </>
  );
}

export default PrescriptionEmission;
